import * as React from 'react';

import { GenericFile } from '~/modules/trade/types/file';

type PreviewPhoto = { id: string; url?: string; name?: string };

type UsePhotoPreviewReturn = {
  previewOpen: boolean;
  currentPhotoIndex: number;
  previewPhotos: PreviewPhoto[];
  handlePhotoView: (fileId: string) => void;
  closePreview: () => void;
  navigatePreview: (index: number) => void;
};

export const usePhotoPreview = (
  photoFiles: GenericFile[],
): UsePhotoPreviewReturn => {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);

  const previewPhotos = React.useMemo<PreviewPhoto[]>(
    () =>
      photoFiles
        .filter((file) => !!file.downloadUrl)
        .map((file) => ({
          id: file.id,
          url: file.downloadUrl,
          name: file.fileName,
        })),
    [photoFiles],
  );

  React.useEffect(() => {
    if (previewPhotos.length === 0) {
      if (previewOpen) setPreviewOpen(false);
      if (currentPhotoIndex !== 0) setCurrentPhotoIndex(0);
      return;
    }
    if (currentPhotoIndex >= previewPhotos.length) {
      setCurrentPhotoIndex(previewPhotos.length - 1);
    }
  }, [previewPhotos.length, currentPhotoIndex, previewOpen]);

  const handlePhotoView = React.useCallback(
    (fileId: string) => {
      const index = previewPhotos.findIndex((p) => p.id === fileId);
      if (index >= 0) {
        setCurrentPhotoIndex(index);
        setPreviewOpen(true);
      }
    },
    [previewPhotos],
  );

  const closePreview = React.useCallback(() => {
    setPreviewOpen(false);
  }, []);

  return {
    previewOpen,
    currentPhotoIndex,
    previewPhotos,
    handlePhotoView,
    closePreview,
    navigatePreview: setCurrentPhotoIndex,
  };
};
